// Pancha Element Engine

export const ELEMENTS = {
  fire: { name: "Fire", sanskrit: "Agni", symbol: "Agni" },
  water: { name: "Water", sanskrit: "Jal", symbol: "Jal" },
  earth: { name: "Earth", sanskrit: "Prithvi", symbol: "Prithvi" },
  air: { name: "Air", sanskrit: "Vayu", symbol: "Vayu" },
  ether: { name: "Ether", sanskrit: "Akasha", symbol: "Akasha" },
};

// Unicode symbols (kept separate so we avoid emoji per guidelines; using simple glyphs)
export const ELEMENT_GLYPH = {
  fire: "\u25B2",   // triangle up
  water: "\u25BD",  // triangle down
  earth: "\u25A0",  // square
  air: "\u25CB",    // circle
  ether: "\u2736",  // star
};

const LIFE_PATH_ELEMENT = {
  1: "fire", 2: "water", 3: "fire", 4: "earth", 5: "air",
  6: "earth", 7: "ether", 8: "earth", 9: "water",
  11: "ether", 22: "earth", 33: "ether",
};

const NAME_NUMBER_ELEMENT = {
  1: "fire", 2: "water", 3: "air", 4: "earth", 5: "air",
  6: "water", 7: "ether", 8: "earth", 9: "fire",
  11: "ether", 22: "earth",
};

const ZODIAC_ELEMENT = {
  Aries: "fire", Leo: "fire", Sagittarius: "fire",
  Taurus: "earth", Virgo: "earth", Capricorn: "earth",
  Gemini: "air", Libra: "air", Aquarius: "air",
  Cancer: "water", Scorpio: "water", Pisces: "water",
};

const PANCHA_SETS = {
  fire: {
    earth: "Red Jasper", water: "Aquamarine", fire: "Carnelian",
    air: "Clear Quartz", ether: "Amethyst",
  },
  earth: {
    earth: "Black Tourmaline", water: "Moonstone", fire: "Pyrite",
    air: "Sodalite", ether: "Selenite",
  },
  air: {
    earth: "Smoky Quartz", water: "Blue Lace Agate", fire: "Sunstone",
    air: "Lapis Lazuli", ether: "Fluorite",
  },
  water: {
    earth: "Jade", water: "Rose Quartz", fire: "Tiger Eye",
    air: "Yellow Topaz", ether: "Labradorite",
  },
  ether: {
    earth: "Bloodstone", water: "Pearl", fire: "Citrine",
    air: "Celestite", ether: "Moonstone",
  },
};

const ELEMENT_ORDER = ["earth", "water", "fire", "air", "ether"];

export function elementForLifePath(lp) {
  return LIFE_PATH_ELEMENT[lp] || null;
}

export function elementForNameNumber(nn) {
  return NAME_NUMBER_ELEMENT[nn] || null;
}

export function elementForZodiac(z) {
  return ZODIAC_ELEMENT[z] || null;
}

export function determineDominantElement(lifePath, nameNumber, zodiac) {
  const lpEl = elementForLifePath(lifePath);
  const nnEl = elementForNameNumber(nameNumber);
  const zEl = elementForZodiac(zodiac);
  const counts = {};
  [lpEl, nnEl, zEl].forEach((el) => {
    if (!el) return;
    counts[el] = (counts[el] || 0) + 1;
  });
  let maxCount = 0;
  Object.values(counts).forEach((c) => {
    if (c > maxCount) maxCount = c;
  });
  const top = Object.keys(counts).filter((k) => counts[k] === maxCount);
  if (top.length === 1) return { dominant: top[0], counts, tied: false };
  // Tiebreaker: Life Path element
  if (lpEl && top.includes(lpEl)) return { dominant: lpEl, counts, tied: true };
  return { dominant: top[0], counts, tied: true };
}

export function selectPanchaBeads(dominantElement) {
  const set = PANCHA_SETS[dominantElement];
  if (!set) return [];
  return ELEMENT_ORDER.map((el) => ({
    element: el,
    elementName: ELEMENTS[el].name,
    sanskrit: ELEMENTS[el].sanskrit,
    glyph: ELEMENT_GLYPH[el],
    crystal: set[el],
    beadCount: 1,
    source: "Pancha Foundation",
    reason: `${ELEMENTS[el].name} (${ELEMENTS[el].sanskrit}) bead chosen for your dominant element ${dominantElement.charAt(0).toUpperCase() + dominantElement.slice(1)}`,
  }));
}

export function whyDominant(dominantElement, lifePath, nameNumber, zodiac) {
  // Guard against legacy/blank/wrong-case values so this helper never
  // crashes the admin page. Unknown elements fall back to a neutral copy.
  const rawEl = dominantElement || "";
  const el = rawEl.toString().toLowerCase();
  const elDef = ELEMENTS[el];
  if (!elDef) {
    return "Your bracelet's dominant element is not yet recorded on this order.";
  }
  const lpEl = elementForLifePath(lifePath);
  const nnEl = elementForNameNumber(nameNumber);
  const zEl = elementForZodiac(zodiac);
  const sources = [];
  if (lpEl === el) sources.push(`your Life Path ${lifePath}`);
  if (nnEl === el) sources.push(`your Name Number ${nameNumber}`);
  if (zEl === el) sources.push(`your Zodiac ${zodiac}`);
  const prettyEl = elDef.name;
  if (sources.length === 0) {
    return `${prettyEl} (${elDef.sanskrit}) emerged as the anchoring element for your bracelet.`;
  }
  return `${prettyEl} (${elDef.sanskrit}) is your dominant element because it appears in ${sources.join(" and ")}.`;
}
